import { useCallback, useEffect, useMemo, useState } from 'react'
import { ApiError } from '../api/client'
import {
  callCenterSetupApi,
  type LeadCallScript,
  type LeadQualification,
  type QualificationQuestion,
} from '../api/callCenterSetup'
import { fetchProducts, type Product } from '../api/catalog'
import { Button, Callout, Card, CardContent, CardHeader, KeyValueList, Select, StatusPill, TextField } from './ui'

/**
 * F-088 (TASK-0342): pannello della scheda lead con lo script di chiamata e il questionario di qualificazione.
 * Lo script è il più specifico per campagna, settore e tipo cliente (ripiego: testo libero della campagna). Il
 * questionario è quello attivo applicabile al lead; le domande condizionali compaiono solo se la condizione vale
 * e la risposta di una domanda nascosta non viene inviata. Il server rivalida tutto.
 * Visibile a chi vede il lead (gate ADR-008): un errore di lettura nasconde la sezione, non rompe la pagina.
 */

function norm(value: string): string {
  const v = value.trim().toLowerCase()
  return v === 'sì' ? 'si' : v
}

/** Domande visibili in base alle risposte correnti (stesso criterio del server). */
function visibleQuestions(questions: QualificationQuestion[], answers: Record<string, string>): QualificationQuestion[] {
  const visible: QualificationQuestion[] = []
  const shown = new Set<string>()
  for (const q of questions) {
    if (q.showIf) {
      const given = shown.has(q.showIf.code) ? answers[q.showIf.code] : undefined
      if (given === undefined || given.trim() === '' || norm(given) !== norm(q.showIf.equals)) continue
    }
    visible.push(q)
    shown.add(q.code)
  }
  return visible
}

function fmtDateTime(s: string): string {
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleString('it-IT', { timeZone: 'Europe/Rome' })
}

const SOURCE_LABEL: Record<string, string> = {
  script: 'Script configurato',
  campaign: 'Testo libero della campagna',
  none: 'Nessuno script',
}

/**
 * `compact` (F-091, Postazione Operatore): niente storico dei questionari precedenti e script piu' basso,
 * perche' il pannello sta sotto la scheda del prossimo lead.
 */
export function LeadCallCenterPanel({ leadId, compact = false }: { leadId: number; compact?: boolean }) {
  const [script, setScript] = useState<LeadCallScript | null>(null)
  const [qualification, setQualification] = useState<LeadQualification | null>(null)
  const [answers, setAnswers] = useState<Record<string, string>>({})
  // F-143 (REQ-092-001): prodotto scelto dalla postazione; rilegge lo script (vuoto = solo i prodotti delle campagne).
  const [products, setProducts] = useState<Product[]>([])
  const [productId, setProductId] = useState<number | ''>('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ kind: 'success' | 'danger'; text: string[] } | null>(null)

  const load = useCallback(async () => {
    const [s, q] = await Promise.allSettled([
      callCenterSetupApi.scripts.forLead(leadId, productId === '' ? undefined : productId),
      callCenterSetupApi.qualification.forLead(leadId),
    ])
    setScript(s.status === 'fulfilled' ? s.value : null)
    if (q.status === 'fulfilled') {
      setQualification(q.value)
      const existing = q.value.form ? q.value.answers.find(a => a.formId === q.value.form!.id) : undefined
      setAnswers(existing?.answers ?? {})
    } else {
      setQualification(null)
    }
  }, [leadId, productId])

  useEffect(() => { void load() }, [load])
  useEffect(() => {
    fetchProducts({ pageSize: 200, isOption: false }).then(r => setProducts(r.items)).catch(() => setProducts([]))
  }, [])

  const form = qualification?.form ?? null
  const visible = useMemo(
    () => (form ? visibleQuestions(form.questions, answers) : []),
    [form, answers],
  )
  const otherAnswers = compact ? [] : (qualification?.answers ?? []).filter(a => a.formId !== form?.id)

  async function save() {
    if (!form) return
    const payload: Record<string, string> = {}
    for (const q of visible) {
      const v = answers[q.code]
      if (v !== undefined && v.trim() !== '') payload[q.code] = v
    }
    setSaving(true)
    setMessage(null)
    try {
      await callCenterSetupApi.qualification.save(leadId, form.id, payload)
      setMessage({ kind: 'success', text: ['Risposte salvate.'] })
      await load()
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { errors?: string[] } | undefined) : undefined
      setMessage({
        kind: 'danger',
        text: body?.errors && body.errors.length > 0 ? body.errors : [e instanceof Error ? e.message : 'Errore durante il salvataggio'],
      })
    } finally {
      setSaving(false)
    }
  }

  function input(q: QualificationQuestion) {
    const value = answers[q.code] ?? ''
    const set = (v: string) => setAnswers(a => ({ ...a, [q.code]: v }))
    const label = `${q.label}${q.required ? ' *' : ''}`
    switch (q.type) {
      case 'SiNo':
        return (
          <Select label={label} value={value} onChange={e => set(e.target.value)}>
            <option value="">—</option>
            <option value="Si">Sì</option>
            <option value="No">No</option>
          </Select>
        )
      case 'Elenco':
        return (
          <Select label={label} value={value} onChange={e => set(e.target.value)}>
            <option value="">—</option>
            {(q.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
          </Select>
        )
      case 'Numero':
        return <TextField label={label} inputMode="decimal" value={value} onChange={e => set(e.target.value)} />
      case 'Data':
        return <TextField label={label} type="date" value={value} onChange={e => set(e.target.value)} />
      default:
        return <TextField label={label} maxLength={1000} value={value} onChange={e => set(e.target.value)} />
    }
  }

  const hasScript = script !== null && script.source !== 'none'
  if (!hasScript && !form && otherAnswers.length === 0 && products.length === 0) return null

  return (
    <Card variant="outlined" className={compact ? 'mt-3' : undefined}>
      <CardHeader title="Script e questionario" />
      <CardContent>
        {products.length > 0 && (
          <div className="mb-2">
            <Select id={`lcc-product-${leadId}`} label="Prodotto" value={productId}
              onChange={e => setProductId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Quello della campagna</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </div>
        )}
        {hasScript && script && (
          <div className="mb-3">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="md-typescale-emphasized-body-medium">{script.name ?? 'Script'}</span>
              <StatusPill tone="neutral">{SOURCE_LABEL[script.source]}</StatusPill>
            </div>
            <Card as="div" variant="filled" padding="sm">
              <pre className={`mb-0 md-typescale-body-small md-u-pre-wrap ${compact ? 'md-u-scroll-y-180' : 'md-u-scroll-y-320'}`}>
                {script.body}
              </pre>
            </Card>
          </div>
        )}

        {form && (
          <div>
            <div className="mb-2 md-typescale-emphasized-body-medium">
              {form.name} <span className="text-muted md-typescale-body-small">v{form.version}</span>
            </div>
            {visible.map(q => (
              <div className="mb-2" key={q.code}>
                {input(q)}
              </div>
            ))}
            {message && (
              <Callout tone={message.kind === 'danger' ? 'error' : 'success'} className="mt-2 mb-2">
                {message.text.map((t, i) => <div key={i}>{t}</div>)}
              </Callout>
            )}
            <Button variant="filled" size="sm" disabled={saving} onClick={() => void save()}>
              {saving ? 'Salvataggio...' : 'Salva risposte'}
            </Button>
          </div>
        )}

        {otherAnswers.length > 0 && (
          <div className={form ? 'mt-3 pt-3 border-top' : ''}>
            <div className="mb-1 md-typescale-emphasized-body-medium">Risposte a questionari precedenti</div>
            {otherAnswers.map(a => (
              <div className="mb-2 md-typescale-body-small" key={a.formId}>
                <div className="text-muted">{a.formName ?? `Questionario #${a.formId}`} v{a.formVersion} · {fmtDateTime(a.answeredAt)}</div>
                <KeyValueList
                  items={(a.questions ?? []).filter(q => a.answers[q.code] !== undefined).map(q => ({ label: q.label, value: a.answers[q.code] }))}
                />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
